import type { SvgIconComponent } from "@mui/icons-material";
import {
  AdminPanelSettings,
  AdminPanelSettingsOutlined,
  People,
  PeopleAlt,
  PeopleAltOutlined,
  PeopleOutline,
  QrCode,
  QrCodeOutlined,
} from "@mui/icons-material";
import TablePlanningIcon from "@mui/icons-material/AlignHorizontalLeft";
import TablePlanningIconOutlined from "@mui/icons-material/AlignHorizontalLeftOutlined";
import BarChartIcon from "@mui/icons-material/BarChart";
import BarChartIconOutlined from "@mui/icons-material/BarChartOutlined";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DashboardIconOutlined from "@mui/icons-material/DashboardOutlined";
import MenuBook from "@mui/icons-material/MenuBook";
import MenuBookOutlined from "@mui/icons-material/MenuBookOutlined";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import PointOfSaleIconOutlined from "@mui/icons-material/PointOfSaleOutlined";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import TableRestaurantOutlined from "@mui/icons-material/TableRestaurantOutlined";
import SettingsIcon from "@mui/icons-material/Tune";
import SettingsIconOutlined from "@mui/icons-material/TuneOutlined";
import GlobeIcon from "@mui/icons-material/Web";
import { ModuleTypes } from "@repo/database";
import { useTranslation } from "react-i18next";

import { useUser } from "~/hooks/use-user";

export interface NavigationItem {
  name: string;
  href: string;
  section: string;
  searchStrings?: string[];
  icon?: SvgIconComponent;
  currentIcon?: SvgIconComponent;
  position?: number;
}

export const useNavigationManager = () => {
  const { modules } = useUser();

  const { t } = useTranslation("common");

  const NAV_SECTIONS = {
    top: "top",
    bottom: "bottom",
  } as const;

  const REPORTS_NAV_SECTIONS = {
    general: t("general"),
    sales: t("sales"),
  } as const;

  const SETTINGS_NAV_SECTIONS = {
    general: t("general"),
    products: t("products"),
    devices: t("Device.devices"),
    cash_register: t("cash_register"),
    integrations: t("integrations"),
    communication: t("communication"),
    employees: t("employees"),
    floor_plan: t("floor_plan"),
    online_orders: t("online_orders"),
    bookings: t("bookings"),
    account: t("Account.page_title"),
  } as const;

  let settingsNavItems: NavigationItem[] = [
    {
      name: t("general"),
      href: "/settings",
      section: SETTINGS_NAV_SECTIONS.general,
      searchStrings: ["general", "settings"],
    },
    {
      name: t("company"),
      href: "/settings/company",
      section: SETTINGS_NAV_SECTIONS.general,
      searchStrings: ["company", "bedrijf"],
    },
    {
      name: t("Integrations.page_title"),
      href: "/settings/integrations",
      section: SETTINGS_NAV_SECTIONS.general,
      searchStrings: ["integrations", "integraties"],
    },
    {
      name: t("email"),
      href: "/settings/email",
      section: SETTINGS_NAV_SECTIONS.general,
      searchStrings: ["email", "smtp", "server"],
    },
    {
      name: t("prices"),
      href: "/settings/prices",
      section: SETTINGS_NAV_SECTIONS.general,
      searchStrings: ["prices", "prijzen", "prijs"],
    },
    {
      name: t("products"),
      href: "/settings/products",
      section: SETTINGS_NAV_SECTIONS.products,
      searchStrings: ["products", "producten"],
    },
    {
      name: t("ProductModifier.product_modifiers"),
      href: "/settings/modifiers",
      section: SETTINGS_NAV_SECTIONS.products,
      searchStrings: ["products", "modifiers"],
    },
    {
      name: t("ProductModifierGroup.product_modifier_groups"),
      href: "/settings/modifier-groups",
      section: SETTINGS_NAV_SECTIONS.products,
      searchStrings: ["modifiers", "modifier groups", "extra"],
    },
    {
      name: t("product_categories"),
      href: "/settings/product-categories",
      section: SETTINGS_NAV_SECTIONS.products,
      searchStrings: ["product categories", "productcategorieën"],
    },
    {
      name: t("courses"),
      href: "/settings/courses",
      section: SETTINGS_NAV_SECTIONS.products,
      searchStrings: ["courses", "gangen"],
    },
    {
      name: t("base_stations"),
      href: "/settings/devices/base-stations",
      section: SETTINGS_NAV_SECTIONS.devices,
      searchStrings: ["base stations"],
    },
    {
      name: t("printers"),
      href: "/settings/devices/printers",
      section: SETTINGS_NAV_SECTIONS.devices,
      searchStrings: ["printers"],
    },
    {
      name: t("payment_terminals"),
      href: "/settings/devices/payment-terminals",
      section: SETTINGS_NAV_SECTIONS.devices,
      searchStrings: ["terminals", "payment", "pin"],
    },
    {
      name: t("employees"),
      href: "/settings/employees",
      section: SETTINGS_NAV_SECTIONS.employees,
      searchStrings: [
        "employees",
        "werknemers",
        "medewerkers",
        "personeel",
        "staff",
        "gebruikers",
        "users",
      ],
    },
    {
      name: t("employee_groups"),
      href: "/settings/employee-groups",
      section: SETTINGS_NAV_SECTIONS.employees,
      searchStrings: [
        "employee groups",
        "werknemers",
        "medewerkers",
        "personeel",
        "staff",
        "gebruikers",
        "users",
      ],
    },
    {
      name: t("appearance"),
      href: "/settings/appearance",
      section: SETTINGS_NAV_SECTIONS.general,
      searchStrings: [
        "appearance",
        "dark mode",
        "light mode",
        "color",
        "interface",
      ],
    },
    {
      name: t("payment_methods"),
      href: "/settings/payment-methods",
      section: SETTINGS_NAV_SECTIONS.general,
      searchStrings: ["payment methods", "betaalmethodes"],
    },
    {
      name: t("CashLocation.cash_locations"),
      href: "/settings/cash-locations",
      section: SETTINGS_NAV_SECTIONS.general,
      searchStrings: ["cash locations", "geld locaties", "bank", "lade"],
    },
    {
      name: t("vat_rates"),
      href: "/settings/vat-rates",
      section: SETTINGS_NAV_SECTIONS.general,
      searchStrings: ["vat rates", "btw tarieven", "btw tarief"],
    },
    {
      name: t("Labels.nav_title"),
      href: "/settings/labels",
      section: SETTINGS_NAV_SECTIONS.general,
      searchStrings: ["labels"],
    },
    {
      name: t("handhelds"),
      href: "/settings/devices/handhelds",
      section: SETTINGS_NAV_SECTIONS.devices,
      searchStrings: ["handhelds"],
    },
    {
      name: t("messages"),
      href: "/settings/communication/messages",
      section: SETTINGS_NAV_SECTIONS.communication,
      searchStrings: ["messages", "berichten"],
    },
    {
      name: t("Account.page_title"),
      href: "/settings/account",
      section: SETTINGS_NAV_SECTIONS.account,
      searchStrings: ["billing", "account"],
    },
    {
      name: t("Account.invoices_page_title"),
      href: "/settings/account/invoices",
      section: SETTINGS_NAV_SECTIONS.account,
      searchStrings: ["billing", "invoices", "facturen", "account"],
    },
    {
      name: t("Account.contracts_page_title"),
      href: "/settings/account/contracts",
      section: SETTINGS_NAV_SECTIONS.account,
      searchStrings: ["contracts", "contracten", "account"],
    },
  ];

  const reportsNavItems: NavigationItem[] = [
    {
      name: t("Report.receipts"),
      href: "/reports/receipt",
      section: REPORTS_NAV_SECTIONS.sales,
      searchStrings: ["receipts"],
    },

    {
      name: t("Report.labour"),
      href: "/reports/labour",
      section: REPORTS_NAV_SECTIONS.general,
      searchStrings: ["labour"],
    },
    {
      name: t("Report.Mutation.mutations"),
      href: "/reports/mutation",
      section: REPORTS_NAV_SECTIONS.general,
      searchStrings: ["mutations", "mutaties"],
    },
    {
      name: t("Report.day"),
      href: "/reports/day",
      section: REPORTS_NAV_SECTIONS.sales,
      searchStrings: ["day", "dag"],
    },
    {
      name: t("Report.week.page_title"),
      href: "/reports/week",
      section: REPORTS_NAV_SECTIONS.sales,
      searchStrings: ["week"],
    },
    {
      name: t("Report.month.page_title"),
      href: "/reports/month",
      section: REPORTS_NAV_SECTIONS.sales,
      searchStrings: ["month", "maand"],
    },
    {
      name: t("Report.closing.page_title"),
      href: "/reports/closing",
      section: REPORTS_NAV_SECTIONS.sales,
      searchStrings: ["closing", "z", "sluiting"],
    },
    {
      name: t("Report.product"),
      href: "/reports/product",
      section: REPORTS_NAV_SECTIONS.sales,
      searchStrings: ["products", "producten"],
    },
    {
      name: t("Report.category"),
      href: "/reports/category",
      section: REPORTS_NAV_SECTIONS.sales,
      searchStrings: ["categories", "categorieën", "category"],
    },
    {
      name: t("Report.vat"),
      href: "/reports/vat",
      section: REPORTS_NAV_SECTIONS.sales,
      searchStrings: ["vat", "btw"],
    },
    {
      name: t("Report.employee"),
      href: "/reports/employee",
      section: REPORTS_NAV_SECTIONS.sales,
      searchStrings: ["employees", "werknemers", "personeel", "staff"],
    },
  ];

  let navItems: NavigationItem[] = [
    {
      name: t("dashboard"),
      href: "/",
      section: NAV_SECTIONS.top,
      currentIcon: DashboardIcon,
      icon: DashboardIconOutlined,
      position: 1,
    },
    {
      name: t("Customers.page_title"),
      href: "/customers",
      section: NAV_SECTIONS.top,
      currentIcon: PeopleAlt,
      icon: PeopleAltOutlined,
      position: 4,
    },
    {
      name: t("reports"),
      href: "/reports",
      section: NAV_SECTIONS.top,
      currentIcon: BarChartIcon,
      icon: BarChartIconOutlined,
      position: 7,
    },
    {
      name: t("Navigation.management"),
      href: "/settings",
      section: NAV_SECTIONS.bottom,
      currentIcon: SettingsIcon,
      icon: SettingsIconOutlined,
      position: 8,
    },
  ];

  const adminItems: NavigationItem[] = [
    {
      name: t("Admin.Dashboard.page_title"),
      href: "/admin",
      section: NAV_SECTIONS.top,
      currentIcon: AdminPanelSettings,
      icon: AdminPanelSettingsOutlined,
      position: 1,
    },
    {
      name: t("Admin.Merchants.page_title"),
      href: "/admin/merchants",
      section: NAV_SECTIONS.top,
      currentIcon: People,
      icon: PeopleOutline,
      position: 2,
    },
    {
      name: t("Admin.QR.page_title"),
      href: "/admin/qr",
      section: NAV_SECTIONS.top,
      currentIcon: QrCode,
      icon: QrCodeOutlined,
      position: 3,
    },
    {
      name: "Importer",
      href: "/admin/importer",
      section: NAV_SECTIONS.top,
      currentIcon: QrCode,
      icon: QrCodeOutlined,
      position: 4,
    },
  ];

  if (modules.includes(ModuleTypes.booking)) {
    settingsNavItems = [
      ...settingsNavItems,
      {
        name: t("booking_widget"),
        href: "/settings/bookings/booking-widget",
        section: SETTINGS_NAV_SECTIONS.bookings,
        searchStrings: ["bookings", "reserveringen"],
      },
      {
        name: t("shifts"),
        href: "/settings/bookings/shifts",
        section: SETTINGS_NAV_SECTIONS.bookings,
        searchStrings: ["shifts", "openingstijden", "tijden"],
      },
      {
        name: t("Services.page_title"),
        href: "/settings/bookings/services",
        section: SETTINGS_NAV_SECTIONS.bookings,
        searchStrings: ["services", "diensten"],
      },
      {
        name: t("special_days"),
        href: "/settings/bookings/special-days",
        section: SETTINGS_NAV_SECTIONS.bookings,
        searchStrings: ["shifts", "special days", "holidays"],
      },
    ];
    navItems = [
      ...navItems,
      {
        name: t("guest_planning"),
        href: "/bookings/guest-planning",
        section: NAV_SECTIONS.top,
        currentIcon: MenuBook,
        icon: MenuBookOutlined,
        position: 2,
      },
      {
        name: t("table_planning"),
        href: "/bookings/table-planning",
        section: NAV_SECTIONS.top,
        currentIcon: TablePlanningIcon,
        icon: TablePlanningIconOutlined,
        position: 3,
      },
    ];
  }

  if (modules.includes(ModuleTypes.cash_register)) {
    navItems.push({
      name: t("cash_register"),
      href: "/cash-register",
      section: NAV_SECTIONS.top,
      currentIcon: PointOfSaleIcon,
      icon: PointOfSaleIconOutlined,
      position: 5,
    });
    settingsNavItems = [
      ...settingsNavItems,
      {
        name: t("print_routes"),
        href: "/settings/print-routes",
        section: SETTINGS_NAV_SECTIONS.cash_register,
        searchStrings: ["print routes"],
      },
      {
        name: t("cash_register"),
        href: "/settings/cash-register",
        section: SETTINGS_NAV_SECTIONS.cash_register,
        searchStrings: ["cash register", "kassa"],
      },
      {
        name: t("tickets"),
        href: "/settings/cash-register/ticket",
        section: SETTINGS_NAV_SECTIONS.cash_register,
        searchStrings: ["receipt", "bon"],
      },
    ];
  }

  if (modules.includes(ModuleTypes.table)) {
    settingsNavItems = [
      ...settingsNavItems,
      {
        name: t("sections"),
        href: "/settings/sections",
        section: SETTINGS_NAV_SECTIONS.floor_plan,
        searchStrings: ["sections", "secties", "locaties"],
      },
      {
        name: t("tables"),
        href: "/settings/tables",
        section: SETTINGS_NAV_SECTIONS.floor_plan,
        searchStrings: ["tables", "tafels"],
      },
      {
        name: t("TableCombinations.nav_title"),
        href: "/settings/table-combinations",
        section: SETTINGS_NAV_SECTIONS.floor_plan,
        searchStrings: ["tables", "tafels", "combinations", "combinaties"],
      },
      {
        name: t("floor_plan_editor"),
        href: "/settings/floor-plan",
        section: SETTINGS_NAV_SECTIONS.floor_plan,
        searchStrings: ["floor plan", "tafels", "tables"],
      },
    ];
    navItems = [
      ...navItems,
      {
        name: t("floor_plan"),
        href: "/floor-plan",
        section: NAV_SECTIONS.top,
        currentIcon: TableRestaurantIcon,
        icon: TableRestaurantOutlined,
        position: 6,
      },
    ];
  }

  if (modules.includes(ModuleTypes.ordering)) {
    settingsNavItems = [
      ...settingsNavItems,
      {
        name: t("online_orders"),
        href: "/settings/online-orders",
        section: SETTINGS_NAV_SECTIONS.online_orders,
        searchStrings: ["online_orders"],
      },
      {
        name: t("OnlineOrders.Shifts.nav_title"),
        href: "/settings/online-orders/shifts",
        section: SETTINGS_NAV_SECTIONS.online_orders,
        searchStrings: [
          "online orders",
          "shifts",
          "online bestellingen",
          "tijden",
        ],
      },
    ];
    navItems = [
      ...navItems,
      {
        name: t("online_orders"),
        href: "/online-orders",
        section: NAV_SECTIONS.top,
        icon: GlobeIcon,
        position: 7,
      },
    ];
  }

  return {
    navItems,
    settingsNavItems,
    reportsNavItems,
    adminItems,
    SETTINGS_NAV_SECTIONS,
  };
};
